import {
  Mesh,
  SphereGeometry,
  TorusGeometry,
  ConeGeometry,
  CylinderGeometry,
  CircleGeometry,
  PlaneGeometry,
  Vector3
} from 'three';
import { Ability } from './Ability.js';
import { settings } from '../config/settings.js';
import { ParticleShape } from '../particles/ParticleSystem.js';
import { RateEmitter } from '../particles/ParticleEngine.js';
import { BurstMode } from '../effects/BurstSphere.js';
import { DecalType } from '../effects/GroundDecals.js';
import { frame } from '../core/FrameUniforms.js';
import { LAYER } from '../core/Layers.js';
import { saturate, Easing } from '../utils/math.js';
import { getColor } from '../utils/color.js';
import {
  ConstructStyle,
  ConstructGlyph,
  createConstructMaterial,
  createConstructGlyphMaterial,
  syncConstructMaterial
} from '../materials/ConstructMaterial.js';
import { poseCylinder, pointOnRing } from './ConstructUtils.js';

const NODES = 12;
const _centreTop = new Vector3();
const _rimPoint = new Vector3();
const _dir = new Vector3();
const _emit = {};

/**
 * SUNWHEEL — a moving sector hazard.
 *
 * Its area is not simply on or off. A bright hand sweeps around a readable dial
 * and the dangerous sector travels with it, forcing continuous repositioning.
 * Cardinal beats punctuate the rotation so timing can be learned, while the
 * shadow behind the hand remains the visual promise of the safer side.
 */
export class SunwheelAbility extends Ability {
  constructor(context) {
    super('sunwheel', context);
  }

  createShaders() {
    this.seedGeometry = new SphereGeometry(1, 24, 16);
    this.solarMaterial = createConstructMaterial(ConstructStyle.SOLAR);
    this.seed = new Mesh(this.seedGeometry, this.solarMaterial);
    this.seed.layers.set(LAYER.VFX);
    this.seed.renderOrder = 13;
    this.group.add(this.seed);

    this.ringGeometry = new TorusGeometry(1, 0.045, 8, 72);
    this.ringMaterial = createConstructMaterial(ConstructStyle.SOLAR);
    this.ring = new Mesh(this.ringGeometry, this.ringMaterial);
    this.ring.layers.set(LAYER.VFX);
    this.ring.renderOrder = 12;
    this.ring.rotation.x = Math.PI / 2;
    this.group.add(this.ring);

    this.gnomonGeometry = new ConeGeometry(1, 1, 5, 1, true);
    this.gnomonMaterial = createConstructMaterial(ConstructStyle.SOLAR);
    this.gnomon = new Mesh(this.gnomonGeometry, this.gnomonMaterial);
    this.gnomon.layers.set(LAYER.VFX);
    this.gnomon.renderOrder = 12;
    this.group.add(this.gnomon);

    this.handGeometry = new CylinderGeometry(1, 1, 1, 8, 1, true);
    this.handMaterial = createConstructMaterial(ConstructStyle.ENERGY);
    this.hand = new Mesh(this.handGeometry, this.handMaterial);
    this.hand.layers.set(LAYER.VFX);
    this.hand.renderOrder = 13;
    this.hand.matrixAutoUpdate = false;
    this.group.add(this.hand);

    this.sectorGeometry = new CircleGeometry(1, 48, -0.5, 1);
    this.sectorMaterial = createConstructMaterial(ConstructStyle.SOLAR);
    this.sector = new Mesh(this.sectorGeometry, this.sectorMaterial);
    this.sector.layers.set(LAYER.VFX);
    this.sector.renderOrder = 9;
    this.sector.rotation.x = -Math.PI / 2;
    this.group.add(this.sector);

    this.fieldGeometry = new PlaneGeometry(1, 1).rotateX(-Math.PI / 2);
    this.fieldMaterial = createConstructGlyphMaterial(ConstructGlyph.SOLAR);
    this.field = new Mesh(this.fieldGeometry, this.fieldMaterial);
    this.field.layers.set(LAYER.VFX);
    this.field.renderOrder = 8;
    this.group.add(this.field);

    this.nodeGeometry = new SphereGeometry(1, 10, 7);
    this.nodeMaterial = createConstructMaterial(ConstructStyle.SOLAR);
    this.nodes = [];
    for (let i = 0; i < NODES; i++) {
      const mesh = new Mesh(this.nodeGeometry, this.nodeMaterial);
      mesh.layers.set(LAYER.VFX);
      mesh.renderOrder = 12;
      this.group.add(mesh);
      this.nodes.push(mesh);
    }

    this.centre = new Vector3();
    this._seed = 0;
    this._beat = -1;
    this._finale = false;
  }

  createParticles() {
    this.embers = this.ctx.particles.get('sunwheel.embers', {
      capacity: 3800,
      shape: ParticleShape.STREAK,
      additive: true,
      stretch: true,
      swirl: true,
      softFade: 0.35
    });
    this.embers.uniforms.uGravity.value.set(0, 0.35, 0);
    this.embers.uniforms.uDrag.value = 0.9;
    this.embers.uniforms.uSwirl.value = 2.2;
    this.embers.uniforms.uEndSize.value = 0.12;
    this.embers.uniforms.uFadeOut.value = 0.4;
    this.emberEmitter = new RateEmitter();
  }

  get impactDuration() {
    const c = settings.sunwheel;
    return Math.max(0.2, (c.openTime + c.sweepTime + c.finalHold) * settings.global.lifetime);
  }

  get fadeDuration() {
    return Math.max(0.1, settings.sunwheel.fadeTime);
  }

  get instanceCount() {
    return NODES + 6;
  }

  onSpawn() {
    this._seed = Math.random() * 10;
    this._beat = -1;
    this._finale = false;
    this.emberEmitter.reset();
    this.seed.visible = true;
    for (const object of [this.ring, this.gnomon, this.hand, this.sector, this.field]) object.visible = false;
    for (const node of this.nodes) node.visible = false;
    this._syncSeed();
  }

  _syncSeed() {
    const c = settings.sunwheel;
    this.seed.position.copy(this.position).setY(c.travelHeight);
    this.seed.scale.setScalar(c.seedSize * (0.82 + Math.sin(this.age * 18) * 0.12));
    syncConstructMaterial(this.solarMaterial, c, {
      seed: this._seed,
      reveal: 1,
      fade: 1,
      pulse: 0.7
    });
  }

  onTravel(dt) {
    this._syncSeed();
    const count = this.emberEmitter.tick(dt, settings.sunwheel.trailRate);
    if (count > 0) this._emit(this.seed.position, count, _dir.copy(this.direction).multiplyScalar(-1), settings.sunwheel.trailSpeed, settings.sunwheel.trailRadius);
  }

  onImpact() {
    const c = settings.sunwheel;
    this.pointAt(1, this.centre).setY(0);
    this.position.copy(this.centre).setY(c.gnomonHeight * 0.45);
    this.seed.visible = false;
    for (const object of [this.ring, this.gnomon, this.hand, this.sector, this.field]) object.visible = true;
    for (const node of this.nodes) node.visible = true;
    this.lightBoost = c.arrivalLight;

    this.ctx.decals.spawn(DecalType.SHOCKWAVE, this.centre, {
      radius: c.zoneRadius,
      life: c.openTime + 0.45,
      intensity: c.fieldIntensity,
      colorA: getColor(c.colorA),
      colorB: getColor(c.colorC)
    });
  }

  _emit(position, count, direction, speed, radius = 0.1) {
    const c = settings.sunwheel;
    const g = settings.global;
    _emit.position = position;
    _emit.radius = radius;
    _emit.direction = direction;
    _emit.speed = speed * g.particleSpeed;
    _emit.speedVariance = 0.5;
    _emit.spread = 0.45;
    _emit.inherit = null;
    _emit.anchor = this.centre;
    _emit.size = c.particleSize * g.particleSize;
    _emit.sizeVariance = 0.6;
    _emit.life = c.particleLife * g.particleLifetime;
    _emit.lifeVariance = 0.4;
    _emit.spin = 0;
    _emit.tint = null;
    _emit.time = frame.uTime.value;
    this.embers.emit(Math.round(count * g.particleCount), _emit);
  }

  _strikeBeat(angle) {
    const c = settings.sunwheel;
    pointOnRing(_rimPoint, this.centre, c.zoneRadius * 0.78, angle, c.handHeight);
    this._emit(_rimPoint, c.beatParticles, _dir.set(0, 1, 0), c.beatSpeed, c.beatRadius);
    this.ctx.decals.spawn(DecalType.SHOCKWAVE, _rimPoint, {
      radius: c.beatDecalRadius,
      life: c.beatLife,
      intensity: c.beatIntensity,
      width: 0.04,
      colorA: getColor(c.colorA),
      colorB: getColor(c.colorC)
    });
    this.lightBoost = Math.max(this.lightBoost, c.beatLight);
    this.ctx.shake.add(c.beatShake * settings.global.cameraShake, 3.5);
  }

  _finish() {
    if (this._finale) return;
    this._finale = true;
    const c = settings.sunwheel;
    _centreTop.copy(this.centre).setY(c.gnomonHeight * 0.7);
    this.ctx.bursts.spawn(BurstMode.AIR, _centreTop, {
      radius: 0.25,
      endRadius: c.zoneRadius * c.burstRadius,
      life: c.burstLife,
      intensity: c.burstIntensity,
      colorA: getColor(c.colorC),
      colorB: getColor(c.colorA),
      colorC: getColor(c.colorB)
    });
    this.lightBoost = c.impactLight;
    this.ctx.shake.add(c.impactShake * settings.global.cameraShake, c.shakeDuration);
    this.ctx.flash.trigger(getColor(c.colorC), c.impactFlash);
  }

  onFade(dt, t) {
    const c = settings.sunwheel;
    const g = settings.global;
    const fade = t <= 1 ? 1 : 1 - (t - 1);
    const openEnd = c.openTime * g.lifetime;
    const sweepEnd = (c.openTime + c.sweepTime) * g.lifetime;
    const open = Easing.outBack(saturate(this.impactTime / Math.max(0.01, openEnd)));
    const sweepT = saturate((this.impactTime - openEnd) / Math.max(0.01, sweepEnd - openEnd));
    const angle = c.startAngle + sweepT * Math.PI * 2 * c.rotations;

    if (sweepT > 0 && sweepT < 1) {
      const beat = Math.floor(sweepT * c.beats);
      if (beat > this._beat) {
        this._beat = beat;
        this._strikeBeat(angle);
      }
    }
    if (sweepT >= 1) this._finish();

    const radius = c.zoneRadius * open;
    this.ring.position.copy(this.centre).setY(c.rimHeight);
    this.ring.rotation.x = Math.PI / 2;
    this.ring.scale.setScalar(radius);
    syncConstructMaterial(this.ringMaterial, c, { seed: this._seed, reveal: open, fade, pulse: sweepT });

    this.gnomon.position.copy(this.centre).setY(c.gnomonHeight * open * 0.5);
    this.gnomon.scale.set(c.gnomonWidth * open, c.gnomonHeight * open, c.gnomonWidth * open);
    this.gnomon.rotation.y = angle * 0.25;
    syncConstructMaterial(this.gnomonMaterial, c, { seed: this._seed + 1, reveal: open, fade, pulse: sweepT });

    _centreTop.copy(this.centre).setY(c.handHeight);
    pointOnRing(_rimPoint, this.centre, c.zoneRadius * 0.9 * open, angle, c.handHeight);
    poseCylinder(this.hand, _centreTop, _rimPoint, c.handWidth * Math.max(0.05, open));
    syncConstructMaterial(this.handMaterial, c, { seed: this._seed + 2, reveal: open, fade, pulse: 0.5 + 0.5 * Math.sin(sweepT * c.beats * Math.PI) });

    this.sector.position.copy(this.centre).setY(c.sectorHeight);
    this.sector.rotation.set(-Math.PI / 2, 0, angle);
    this.sector.scale.setScalar(c.zoneRadius * c.sectorRadius * open);
    syncConstructMaterial(this.sectorMaterial, c, { seed: this._seed + 3, reveal: open, fade: fade * c.sectorOpacity, pulse: sweepT });

    const fieldSize = c.zoneRadius * 2.4;
    this.field.position.copy(this.centre).setY(c.fieldHeight);
    this.field.scale.set(fieldSize, fieldSize, fieldSize);
    syncConstructMaterial(this.fieldMaterial, c, {
      seed: this._seed,
      reveal: open,
      fade,
      pulse: sweepT,
      sweep: angle,
      arc: c.sweepWidth
    });

    for (let i = 0; i < NODES; i++) {
      const nodeAngle = i / NODES * Math.PI * 2;
      const node = this.nodes[i];
      pointOnRing(node.position, this.centre, radius, nodeAngle, c.nodeHeight);
      const distance = Math.abs(Math.atan2(Math.sin(nodeAngle - angle), Math.cos(nodeAngle - angle)));
      const lit = Math.exp(-distance * distance / Math.max(0.001, c.nodeWake));
      node.scale.setScalar(c.nodeSize * open * (0.7 + lit * 0.8));
    }
    syncConstructMaterial(this.nodeMaterial, c, { seed: this._seed + 4, reveal: open, fade, pulse: sweepT });

    const count = this.emberEmitter.tick(dt, c.holdRate);
    if (count > 0 && open > 0.5) {
      pointOnRing(_rimPoint, this.centre, c.zoneRadius * 0.8, angle, c.handHeight);
      this._emit(_rimPoint, count, _dir.set(0, 1, 0), c.particleSpeed, c.particleRadius);
    }
    this.ctx.shake.rumble(c.holdRumble * open * g.cameraShake, dt);
  }

  onDestroy() {
    this.seed.visible = false;
    for (const object of [this.ring, this.gnomon, this.hand, this.sector, this.field]) object.visible = false;
    for (const node of this.nodes) node.visible = false;
  }

  dispose() {
    this.seedGeometry.dispose();
    this.ringGeometry.dispose();
    this.gnomonGeometry.dispose();
    this.handGeometry.dispose();
    this.sectorGeometry.dispose();
    this.fieldGeometry.dispose();
    this.nodeGeometry.dispose();
    this.solarMaterial.dispose();
    this.ringMaterial.dispose();
    this.gnomonMaterial.dispose();
    this.handMaterial.dispose();
    this.sectorMaterial.dispose();
    this.fieldMaterial.dispose();
    this.nodeMaterial.dispose();
    super.dispose();
  }
}
